import React from 'react';
import { Layers, MapPin, Clock, Calendar, Plane } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения деталей маршрута полета
 */
const FlightSchedule = ({ flightRoutes, results }) => {
  if (!flightRoutes || flightRoutes.length === 0) {
    return null;
  }

  // Разделяем маршруты на прямой и обратный
  const outboundRoutes = flightRoutes.filter(route => route.direction === 'outbound');
  const returnRoutes = flightRoutes.filter(route => route.direction === 'return');

  // Форматируем время полета для отображения
  const formatFlightTime = (hours) => {
    const hoursInt = Math.floor(hours);
    const minutes = Math.round((hours - hoursInt) * 60);
    return `${hoursInt} ч ${minutes > 0 ? minutes + ' мин' : ''}`;
  };

  // Форматируем дату/время для отображения
  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Layers className="w-5 h-5 mr-2 text-blue-500" />
        Детали маршрута
      </h3>

      {/* Панель с вкладками для туда/обратно */}
      {results.isRoundTrip && returnRoutes.length > 0 && (
        <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
          <button
            className="py-2 px-4 text-sm font-medium rounded-t-lg text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400"
          >
            Маршрут туда
          </button>
          <button
            className="py-2 px-4 text-sm font-medium rounded-t-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Маршрут обратно
          </button>
        </div>
      )}

      {/* Контейнер для отображения маршрута */}
      <div className="space-y-4">
        {/* Маршрут туда */}
        <div>
          {outboundRoutes.length > 0 && (
            <div className="relative">
              {/* Вертикальная линия для соединения точек маршрута */}
              <div className="absolute left-4 top-8 bottom-8 w-0.5 bg-blue-200 dark:bg-blue-800"></div>
              
              {outboundRoutes.map((route, index) => (
                <div key={route.segment} className="relative flex mb-6 last:mb-0">
                  {/* Маркер точки */}
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center z-10 
                    ${route.isLayover 
                      ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400 border-2 border-amber-300 dark:border-amber-700' 
                      : index === 0 
                        ? 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 border-2 border-green-300 dark:border-green-700'
                        : index === outboundRoutes.length - 1 && !route.isLayover
                          ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 border-2 border-red-300 dark:border-red-700'
                          : 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-700'
                    }`}
                  >
                    {route.isLayover 
                      ? <Clock className="w-4 h-4" />
                      : index === 0 
                        ? <MapPin className="w-4 h-4" />
                        : index === outboundRoutes.length - 1 && !route.isLayover 
                          ? <MapPin className="w-4 h-4" />
                          : <Plane className="w-4 h-4" />
                    }
                  </div>
                  
                  {/* Содержимое блока */}
                  <div className="ml-4 flex-grow">
                    {route.isLayover ? (
                      <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                          Пересадка в {route.departureCity}
                        </h4>
                        <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                          <span className="font-medium text-amber-600 dark:text-amber-400">
                            {formatFlightTime(route.layoverDuration)}
                          </span> ожидания
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>Прибытие: {formatDateTime(route.departureTime)}</span>
                          <span>Вылет: {formatDateTime(route.arrivalTime)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                          {index === 0 ? 'Вылет из ' : 'Перелет '} 
                          {route.departureCity} 
                          {index === 0 ? '' : ' в ' + route.arrivalCity}
                        </h4>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Дата и время:</div>
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                              {formatDateTime(route.departureTime)}
                            </div>
                          </div>
                          {index !== 0 && (
                            <div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Прибытие:</div>
                              <div className="text-sm text-gray-700 dark:text-gray-300">
                                {formatDateTime(route.arrivalTime)}
                              </div>
                            </div>
                          )}
                        </div>
                        {index !== 0 && (
                          <div className="flex justify-between text-xs">
                            <div className="flex items-center">
                              <Clock className="w-3 h-3 text-blue-500 mr-1" />
                              <span className="text-blue-600 dark:text-blue-400">
                                {formatFlightTime(route.flightDuration)}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <Plane className="w-3 h-3 text-blue-500 mr-1" />
                              <span className="text-blue-600 dark:text-blue-400">
                                {formatNumber(route.distance, 0)} км
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Маршрут обратно (отображается только если выбран обратный билет) */}
        {results.isRoundTrip && returnRoutes.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <Plane className="w-4 h-4 mr-2 text-blue-500 transform rotate-180" />
              Обратный маршрут
            </h4>

            <div className="relative">
              {/* Вертикальная линия для соединения точек маршрута */}
              <div className="absolute left-4 top-8 bottom-8 w-0.5 bg-blue-200 dark:bg-blue-800"></div>
              
              {returnRoutes.map((route, index) => (
                <div key={route.segment} className="relative flex mb-6 last:mb-0">
                  {/* Маркер точки */}
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center z-10 
                    ${route.isLayover 
                      ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400 border-2 border-amber-300 dark:border-amber-700' 
                      : index === 0 
                        ? 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 border-2 border-green-300 dark:border-green-700'
                        : index === returnRoutes.length - 1 && !route.isLayover
                          ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 border-2 border-red-300 dark:border-red-700'
                          : 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-700'
                    }`}
                  >
                    {route.isLayover 
                      ? <Clock className="w-4 h-4" />
                      : index === 0 
                        ? <MapPin className="w-4 h-4" />
                        : index === returnRoutes.length - 1 && !route.isLayover 
                          ? <MapPin className="w-4 h-4" />
                          : <Plane className="w-4 h-4 transform rotate-180" />
                    }
                  </div>
                  
                  {/* Содержимое блока (аналогично маршруту туда) */}
                  <div className="ml-4 flex-grow">
                    {route.isLayover ? (
                      <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                          Пересадка в {route.departureCity}
                        </h4>
                        <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                          <span className="font-medium text-amber-600 dark:text-amber-400">
                            {formatFlightTime(route.layoverDuration)}
                          </span> ожидания
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>Прибытие: {formatDateTime(route.departureTime)}</span>
                          <span>Вылет: {formatDateTime(route.arrivalTime)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                          {index === 0 ? 'Вылет из ' : 'Перелет '} 
                          {route.departureCity} 
                          {index === 0 ? '' : ' в ' + route.arrivalCity}
                        </h4>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Дата и время:</div>
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                              {formatDateTime(route.departureTime)}
                            </div>
                          </div>
                          {index !== 0 && (
                            <div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Прибытие:</div>
                              <div className="text-sm text-gray-700 dark:text-gray-300">
                                {formatDateTime(route.arrivalTime)}
                              </div>
                            </div>
                          )}
                        </div>
                        {index !== 0 && (
                          <div className="flex justify-between text-xs">
                            <div className="flex items-center">
                              <Clock className="w-3 h-3 text-blue-500 mr-1" />
                              <span className="text-blue-600 dark:text-blue-400">
                                {formatFlightTime(route.flightDuration)}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <Plane className="w-3 h-3 text-blue-500 mr-1" />
                              <span className="text-blue-600 dark:text-blue-400">
                                {formatNumber(route.distance, 0)} км
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Сводная информация */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Общее расстояние</div>
            <div className="font-medium text-blue-600 dark:text-blue-400">{formatNumber(results.totalDistance, 0)} км</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Общее время полета</div>
            <div className="font-medium text-blue-600 dark:text-blue-400">
              {formatFlightTime(results.totalFlightTimeOutbound + (results.isRoundTrip ? results.totalFlightTimeReturn : 0))}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Время пересадок</div>
            <div className="font-medium text-amber-600 dark:text-amber-400">
              {formatFlightTime(results.totalLayoverTimeOutbound + (results.isRoundTrip ? results.totalLayoverTimeReturn : 0))}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Полное время в пути</div>
            <div className="font-medium text-emerald-600 dark:text-emerald-400">
              {formatFlightTime(results.totalJourneyTime)}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default FlightSchedule;